import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const profileRequestRef = useRef(0)

  const fetchProfile = useCallback(async (userId, retries = 4) => {
    const requestId = profileRequestRef.current + 1
    profileRequestRef.current = requestId

    try {
      for (let i = 0; i < retries; i++) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle()

        if (requestId !== profileRequestRef.current) return null

        if (data) {
          // If the user is on a monthly plan, check whether their billing
          // cycle has renewed and reset their quota counter if so.
          // This is a no-op for free/lifetime plans and for users whose
          // cycle hasn't turned over yet.
          if (data.plan && !['free', 'emerging'].includes(data.plan)) {
            supabase.rpc('reset_quota_if_cycle_renewed', { p_user_id: userId })
              .then(({ error: rpcErr }) => {
                if (rpcErr) console.error('Quota reset RPC failed:', rpcErr)
                else {
                  // Re-fetch profile so the updated counter is reflected in state
                  supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
                    .then(({ data: refreshed }) => {
                      if (refreshed && requestId === profileRequestRef.current) {
                        setProfile(refreshed)
                      }
                    })
                }
              })
          }

          // Check if a cancelled plan has now expired — downgrade to free
          if (data.plan_expires_at && new Date(data.plan_expires_at) <= new Date()) {
            supabase.from('profiles').update({
              plan: 'free',
              plan_active: true,
              plan_expires_at: null,
              paypal_subscription_id: null,
            }).eq('id', userId).then(() => {
              supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
                .then(({ data: downgraded }) => {
                  if (downgraded && requestId === profileRequestRef.current) {
                    setProfile(downgraded)
                  }
                })
            })
          }
          setProfile(data)
          setLoading(false)
          return data
        }

        if (error) {
          console.error('Profile fetch failed:', error)
          break
        }

        if (i < retries - 1) await new Promise(r => setTimeout(r, 800))
      }

      if (requestId === profileRequestRef.current) {
        setProfile(null)
        setLoading(false)
      }
      return null
    } catch (err) {
      console.error('Profile fetch crashed:', err)
      if (requestId === profileRequestRef.current) {
        setProfile(null)
        setLoading(false)
      }
      return null
    }
  }, [])

  useEffect(() => {
    let mounted = true

    async function loadInitialSession() {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (!mounted) return
        if (error) throw error

        const u = session?.user ?? null
        setUser(u)
        if (u) {
          fetchProfile(u.id)
        } else {
          profileRequestRef.current += 1
          setProfile(null)
          setLoading(false)
        }
      } catch (err) {
        console.error('Initial auth session failed:', err)
        if (mounted) {
          profileRequestRef.current += 1
          setUser(null)
          setProfile(null)
          setLoading(false)
        }
      }
    }

    loadInitialSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return

      const u = session?.user ?? null
      setUser(u)

      if (u) {
        setLoading(true)
        setTimeout(() => {
          if (mounted) fetchProfile(u.id)
        }, 0)
      } else {
        profileRequestRef.current += 1
        setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      profileRequestRef.current += 1
      subscription.unsubscribe()
    }
  }, [fetchProfile])

  async function signInWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/dashboard' },
    })
    if (error) throw error
  }

  async function signInWithEmail(email, password) {
    return await supabase.auth.signInWithPassword({ email, password })
  }

  async function signUpWithEmail(email, password, fullName) {
    return await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    })
  }

  async function signOut() {
    profileRequestRef.current += 1
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    setLoading(false)
  }

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      signOut, fetchProfile,
      signInWithGoogle, signInWithEmail, signUpWithEmail,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
